import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, Archive, CheckCircle2, XCircle, Clock, Mail, Loader2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminDirectoryPage() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "pending");
  const [taxonomy, setTaxonomy] = useState(null);
  const [busy, setBusy] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/directory", { params: { include_deleted: false } }),
      api.get("/directory/taxonomy"),
    ])
      .then(([resItems, resTax]) => {
        setItems(resItems.data?.items || []);
        setTaxonomy(resTax.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const loadPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const { data } = await api.get("/admin/directory/pending");
      setPending(data?.items || []);
    } catch {
      setPending([]);
    } finally { setPendingLoading(false); }
  }, []);

  useEffect(() => { load(); loadPending(); }, [load, loadPending]);

  const updateTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(params); next.set("tab", t);
    setParams(next, { replace: true });
  };

  const toggle = async (id, field, value) => {
    try {
      await api.patch(`/directory/${id}`, { [field]: value });
      toast.success("Mise à jour effectuée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette fiche ?")) return;
    try {
      await api.delete(`/directory/${id}`);
      toast.success("Fiche supprimée.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    }
  };

  const approve = async (entry) => {
    setBusy((b) => ({ ...b, [entry.id]: "approve" }));
    try {
      await api.post(`/admin/directory/${entry.id}/approve`, {});
      toast.success("Fiche validée et publiée. Email envoyé au demandeur.");
      await Promise.all([loadPending(), load()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy((b) => { const n = { ...b }; delete n[entry.id]; return n; }); }
  };

  const reject = async (entry) => {
    const reason = window.prompt("Motif de refus (envoyé par email au demandeur) :", "Demande incomplète ou hors champ AITO.PF.");
    if (reason === null) return;
    setBusy((b) => ({ ...b, [entry.id]: "reject" }));
    try {
      await api.post(`/admin/directory/${entry.id}/reject`, { reason });
      toast.success("Fiche refusée. Email envoyé au demandeur.");
      await loadPending();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
    finally { setBusy((b) => { const n = { ...b }; delete n[entry.id]; return n; }); }
  };

  const TYPES = [
    { v: "pending", label: "À valider", showBadge: true },
    { v: "boutique", label: "Boutiques & Services" },
    { v: "sante", label: "Santé & Performance" },
    { v: "services", label: "Services aux sportifs" },
  ];

  const filtered = items.filter((it) => it.type === tab && it.submission_status !== "pending");
  const categoryMap = (t) => {
    if (!taxonomy || !taxonomy[t]) return {};
    const map = {};
    (taxonomy[t].categories || []).forEach((c) => { map[c.slug] = c.label; });
    return map;
  };

  const typeSlug = (t) => t === "sante" ? "sante-performance" : t === "boutique" ? "boutiques-services" : "services";

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-4">
        <Link to="/admin" className="text-xs text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Retour admin
        </Link>
      </div>
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl" data-testid="admin-directory-title">Gestion de l'annuaire</h1>
          <p className="text-sm text-muted-foreground">Santé & Performance · Boutiques · Services aux sportifs</p>
        </div>
        <Link to="/creer-fiche-annuaire">
          <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="admin-directory-create">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle fiche
          </Button>
        </Link>
      </div>

      <Tabs value={tab} onValueChange={updateTab}>
        <TabsList>
          {TYPES.map((t) => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}>
              {t.label}
              {t.showBadge && pending.length > 0 && (
                <Badge className="ml-1.5 bg-[hsl(var(--aito-energy))] text-white text-[10px]" data-testid="pending-count">{pending.length}</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ============ PENDING ============ */}
        <TabsContent value="pending" className="mt-4">
          {pendingLoading ? (
            <p className="text-sm text-muted-foreground">Chargement...</p>
          ) : pending.length === 0 ? (
            <Card className="p-8 text-center text-sm text-muted-foreground border-dashed" data-testid="pending-empty">
              <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-500 mb-2" />
              Aucune activité en attente de validation.
            </Card>
          ) : (
            <div className="space-y-2">
              {pending.map((entry) => (
                <Card key={entry.id} className="p-4 border-l-4 border-[hsl(var(--aito-energy))]" data-testid={`pending-entry-${entry.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="w-12 h-12 rounded-md bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                      {entry.logo_url || entry.photo_url ? (
                        <img src={entry.logo_url || entry.photo_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Clock className="h-5 w-5 text-[hsl(var(--aito-energy))]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="font-semibold text-sm">{entry.name}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{entry.type}</Badge>
                        <Badge variant="outline" className="text-[10px]">{categoryMap(entry.type)[entry.category] || entry.category}</Badge>
                      </div>
                      <p className="text-xs text-foreground/80 mt-1 line-clamp-2">{entry.description}</p>
                      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{entry.contact_email}</span>
                        {entry.commune && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{entry.commune}</span>}
                        {entry.contact_phone && <span>· {entry.contact_phone}</span>}
                        {entry.website && <a href={entry.website} target="_blank" rel="noreferrer" className="underline">Site web</a>}
                        {entry.created_at && <span>· Reçu le {new Date(entry.created_at).toLocaleDateString("fr-FR")}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Link to={`/annuaire/${typeSlug(entry.type)}/${entry.slug}/edit`}>
                        <Button size="sm" variant="outline" data-testid={`pending-edit-${entry.id}`}>
                          <Edit className="h-3.5 w-3.5 mr-1" />Modifier
                        </Button>
                      </Link>
                      <Button size="sm" onClick={() => approve(entry)} disabled={!!busy[entry.id]}
                        className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`pending-approve-${entry.id}`}>
                        {busy[entry.id] === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                        Valider
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => reject(entry)} disabled={!!busy[entry.id]}
                        className="border-orange-400 text-orange-700 hover:bg-orange-50" data-testid={`pending-reject-${entry.id}`}>
                        {busy[entry.id] === "reject" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5 mr-1" />}
                        Refuser
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ============ AUTRES TABS (existant) ============ */}
        {["boutique", "sante", "services"].map((t) => (
          <TabsContent key={t} value={t} className="mt-4">
            {loading ? (
              <p className="text-sm text-muted-foreground">Chargement...</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Aucune fiche publiée pour l'instant.</p>
            ) : (
              <div className="space-y-2">
                {filtered.map((entry) => (
                  <Card key={entry.id} className="p-3" data-testid={`admin-entry-${entry.slug}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="w-12 h-12 rounded-md bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                        {entry.logo_url || entry.photo_url ? (
                          <img src={entry.logo_url || entry.photo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-muted-foreground">{entry.name?.slice(0, 2).toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{entry.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[9px]">{categoryMap(t)[entry.category] || entry.category}</Badge>
                          {entry.commune && <span>{entry.commune}</span>}
                          {!entry.published && <Badge variant="secondary" className="text-[9px] bg-orange-500/10 text-orange-700">Brouillon</Badge>}
                          {entry.archived && <Badge variant="secondary" className="text-[9px]">Archivé</Badge>}
                          {!entry.owner_user_id && <Badge variant="outline" className="text-[9px] text-orange-600">Non revendiquée</Badge>}
                          {entry.submission_source === "public_form" && <Badge variant="outline" className="text-[9px]">Soumis publiquement</Badge>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Link to={`/annuaire/${typeSlug(entry.type)}/${entry.slug}`}>
                          <Button variant="ghost" size="sm"><Eye className="h-3.5 w-3.5" /></Button>
                        </Link>
                        <Link to={`/annuaire/${typeSlug(entry.type)}/${entry.slug}/edit`}>
                          <Button variant="outline" size="sm" data-testid={`edit-${entry.slug}`}><Edit className="h-3.5 w-3.5" /></Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggle(entry.id, "published", !entry.published)}
                          data-testid={`toggle-publish-${entry.slug}`}
                          title={entry.published ? "Dépublier" : "Publier"}
                        >
                          {entry.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => toggle(entry.id, "archived", !entry.archived)} title="Archiver">
                          <Archive className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => remove(entry.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {taxonomy && (
        <Card className="p-4 mt-8">
          <h3 className="font-display font-semibold text-sm mb-2">Taxonomie active</h3>
          <div className="grid sm:grid-cols-3 gap-4 text-xs">
            {["sante", "boutique", "services"].map((t) => (
              <div key={t}>
                <div className="font-semibold uppercase text-[10px] tracking-wider text-muted-foreground mb-1">{taxonomy[t]?.label || t}</div>
                <div className="flex flex-wrap gap-1">
                  {(taxonomy[t]?.categories || []).map((c) => (
                    <Badge key={c.slug} variant="outline" className="text-[9px]">{c.label}</Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
